import type * as React from 'react';
import { ShortCode } from '@highereducation/wordpress';
import {
  Tooltip,
  type TooltipContentFormat,
  type TooltipTriggerMode,
} from '@highereducation/hen-ui';

interface TooltipShortcodeProps {
  content?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  'content-format'?: TooltipContentFormat;
  trigger?: TooltipTriggerMode;
  'delay-duration'?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
}

/**
 * TooltipShortcode - Shortcode wrapper for the Tooltip composite.
 *
 * Wrap a focusable trigger (Button, Link, Icon, etc.) to show brief contextual help.
 *
 * @example
 * [hen-ui-tooltip content="This is a tooltip"]
 *   [hen-ui-button variant="textOnly"]Hover or focus me[/hen-ui-button]
 * [/hen-ui-tooltip]
 *
 * @example Markdown tooltip on an icon trigger:
 * [hen-ui-tooltip content="**Bold** definition with a [link](https://example.com)" content-format="markdown" trigger="click"]
 *   [hen-ui-icon icon="info" size="sm" aria-label="More information"][/hen-ui-icon]
 * [/hen-ui-tooltip]
 */
export const TooltipShortcode: ShortCode<TooltipShortcodeProps> = {
  component: ({
    content,
    side,
    'content-format': contentFormat = 'plain',
    trigger = 'hover',
    'delay-duration': delayDuration,
    children,
  }) => {
    if (!content) {
      return <>{children}</>;
    }

    const parsedDelay =
      delayDuration !== undefined ? Number(delayDuration) : undefined;
    const delayDurationProp =
      parsedDelay !== undefined && !Number.isNaN(parsedDelay)
        ? { delayDuration: parsedDelay }
        : {};

    return (
      <Tooltip
        content={content}
        side={side}
        contentFormat={contentFormat}
        trigger={trigger}
        {...delayDurationProp}
      >
        {children}
      </Tooltip>
    );
  },
};
